import {
  type Envelope,
  type Panel,
  type PipelineStatus,
  pipelineStatusSchema,
  type RunningAnimation,
  visibleRunningAnimations,
} from '@ze-great-dashboard/shared/browser'
import { useRef, useState } from 'react'
import { errorPresentation } from './error-presentation.ts'
import { PanelEvidence, PanelFrame, PanelHint, PanelMetadata, PanelStatus } from './PanelFrame.tsx'
import styles from './PipelinePanel.module.css'
import type { PanelProps } from './panel-props.ts'
import { statusPresentation } from './panel-status.ts'
import { formatPipelineActivity } from './pipeline-activity.ts'
import { isRunningFieldAnimation, RunningField } from './RunningField.tsx'
import { RunningFieldTiming } from './RunningFieldTiming.tsx'
import { isLegacyRunningAnimation, RunningProgress } from './RunningProgress.tsx'
import { useRunningTiming } from './running-timing.ts'
import { PipelineAge, UpdateHealth } from './TimeAge.tsx'
import { visualSeed } from './visual-seed.ts'

export function PipelinePanel({ panel, envelope, attentionActive, updateHealth }: PanelProps) {
  if (!envelope)
    return (
      <PanelFrame panel={panel} attentionActive={attentionActive}>
        <PanelEvidence>
          <PanelHint>Loading…</PanelHint>
        </PanelEvidence>
      </PanelFrame>
    )
  if (envelope.state === 'error') {
    const presentation = errorPresentation(envelope.error.kind)
    return (
      <PanelFrame
        panel={panel}
        envelope={envelope}
        attentionActive={attentionActive}
        error
        layout="status-band"
      >
        <div data-panel-anchor="status">
          <PanelStatus glyph="⚠" label={presentation.label} emphasis={presentation.emphasis} />
        </div>
        <PanelEvidence anchor="evidence">
          <PanelHint>{envelope.error.message}</PanelHint>
        </PanelEvidence>
      </PanelFrame>
    )
  }

  const signal = pipelineStatusSchema.safeParse(envelope.signal)
  if (!signal.success)
    return (
      <PanelFrame
        panel={panel}
        envelope={envelope}
        attentionActive={attentionActive}
        error
        layout="status-band"
      >
        <div data-panel-anchor="status">
          <PanelStatus glyph="⚠" label="Invalid signal" />
        </div>
        <PanelEvidence anchor="evidence">
          <PanelHint>The received pipeline status could not be read.</PanelHint>
        </PanelEvidence>
      </PanelFrame>
    )
  return (
    <PipelineSignalPanel
      panel={panel}
      envelope={envelope}
      signal={signal.data}
      updateHealth={updateHealth}
      attentionActive={attentionActive}
    />
  )
}

function PipelineSignalPanel({
  panel,
  envelope,
  signal,
  updateHealth,
  attentionActive,
}: {
  panel: Panel
  envelope: Envelope
  signal: PipelineStatus
  updateHealth?: PanelProps['updateHealth']
  attentionActive?: boolean
}) {
  const presentation = statusPresentation(signal.status)
  const [defaultAnimation, setDefaultAnimation] = useState<RunningAnimation | undefined>(() =>
    signal.status === 'running' ? selectDefaultRunningAnimation() : undefined,
  )
  const previousStatusRef = useRef(signal.status)
  if (signal.status !== previousStatusRef.current) {
    const wasRunning = previousStatusRef.current === 'running'
    previousStatusRef.current = signal.status
    if (panel.running_animation === undefined && !wasRunning && signal.status === 'running') {
      setDefaultAnimation((previousAnimation) => selectDefaultRunningAnimation(previousAnimation))
    }
  }
  const animation = panel.running_animation ?? defaultAnimation ?? 'off'
  const activeRun = signal.status === 'running' && animation !== 'off'
  const timing = useRunningTiming(signal.runStartedAt, signal.estimatedDurationMs)
  const usesField = activeRun && isRunningFieldAnimation(animation)
  const usesLegacyProgress = activeRun && isLegacyRunningAnimation(animation)
  return (
    <PanelFrame
      panel={panel}
      envelope={envelope}
      attentionActive={attentionActive}
      layout={usesField || usesLegacyProgress ? undefined : 'status-band'}
      field={
        usesField ? (
          <RunningField
            animation={animation}
            progress={timing.progress}
            estimatedDurationMs={timing.estimatedDurationMs}
            overdue={timing.overdue}
            indeterminate={!timing.hasEstimate}
            seed={visualSeed(panel.id)}
          />
        ) : undefined
      }
    >
      <div data-panel-anchor="status">
        <PanelStatus status={signal.status} glyph={presentation.glyph} label={presentation.label} />
      </div>
      <PanelEvidence className={styles.details} anchor="evidence">
        {signal.status === 'running' && (
          <PanelMetadata
            glyph="⚙"
            label="Activity"
            value={formatPipelineActivity(signal.activity)}
            compact={{ kind: 'glyph-only' }}
            title={`Activity: ${formatPipelineActivity(signal.activity)}`}
            className={styles.activity}
          />
        )}
        {signal.branch && (
          <PanelMetadata
            glyph="⎇"
            label="Branch"
            value={<span className={styles.branch}>{signal.branch}</span>}
            compact={{
              kind: 'short-value',
              value: <span className={styles.branch}>{signal.branch}</span>,
            }}
            title={`Branch: ${signal.branch}`}
          />
        )}
        {usesField && (
          <RunningFieldTiming
            elapsedMs={timing.elapsedMs}
            estimatedDurationMs={signal.estimatedDurationMs}
            overdue={timing.overdue}
          />
        )}
        {usesLegacyProgress && <RunningProgress animation={animation} timing={timing} />}
        {signal.status !== 'running' && signal.durationMs !== undefined && (
          <PanelMetadata
            glyph="◷"
            label="Duration"
            value={`Took ${formatDuration(signal.durationMs)}`}
            compact={{ kind: 'short-value', value: formatDuration(signal.durationMs) }}
            title={`Duration: Took ${formatDuration(signal.durationMs)}`}
            className={styles.duration}
          />
        )}
        <PipelineAge
          sourceUpdatedAt={signal.sourceUpdatedAt}
          observedAt={envelope.observedAt}
          running={signal.status === 'running'}
          runStartedAt={signal.runStartedAt}
        />
        {updateHealth && <UpdateHealth health={updateHealth} />}
      </PanelEvidence>
    </PanelFrame>
  )
}

function selectDefaultRunningAnimation(previousAnimation?: RunningAnimation) {
  const candidates = visibleRunningAnimations.filter((animation) => animation !== previousAnimation)
  return candidates[Math.floor(Math.random() * candidates.length)] ?? 'telemetry-bloom'
}

function formatDuration(durationMs: number) {
  const totalSeconds = Math.floor(durationMs / 1_000)
  const hours = Math.floor(totalSeconds / 3_600)
  const minutes = Math.floor((totalSeconds % 3_600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}
